/**
 * Arctic Spas Ontario — 3D Spa Builder
 * Own parametric Three.js engine. No Visao, no factory dependency.
 *
 * One ES module in four bands:
 *   DATA      — full active lineup (specs from factory spec sheets) + factory-sampled colours
 *   TEXTURES  — procedural CanvasTextures (marble acrylic, cedar/composite grain, water, ground)
 *   ENGINE    — SpaScene: renderer, studio env, hand-rolled orbit controls, parametric spa generator
 *   UI        — configurator controls, spec panel, URL state, contact-form sync, lazy boot
 *
 * three.js (pinned) is dynamically import()ed inside boot() so nothing heavy
 * loads until the section nears the viewport.
 *
 * Data sources (workspace): Arctic Spas Brand Assets/specs/*.json (dims/weights/capacity),
 * knowledge-base SKU + technical reference (seats/jets/pumps per Series), 2026 brochure
 * (option availability per family), factory Visao swatch icons sampled for material hexes
 * (arctic-spas-3d-builder/tools/sample-swatches.ps1).
 */

const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.180.0/build/three.module.min.js';

/* ================================================================== *
 * DATA
 * ================================================================== */

const FAMILIES = {
  custom: {
    label: 'Custom Series',
    badges: ['FreeHeat™ insulation', 'Forever Floor® included', 'Spa Boy® salt water available', 'Total Access™ cabinet'],
  },
  classic: {
    label: 'Classic Series',
    badges: ['FreeHeat™ insulation', 'Forever Floor® optional', 'Spa Boy® salt water available', 'Mylovac™ cover'],
  },
  core: {
    label: 'Core Series',
    badges: ['HeatLock® insulation', 'Everlast™ floor', 'Spa Boy® available', 'Mylovac™ cover'],
  },
  awp: {
    label: 'All-Weather Pools',
    badges: ['FreeHeat™ insulation', 'Forever Floor® included', 'Spa Boy® salt water available', 'Swim + soak all winter'],
  },
};

const SERIES = {
  prestige: { label: 'Prestige' },
  signature: { label: 'Signature' },
  legend: { label: 'Legend' },
  'legend-select': { label: 'Legend Select' },
};
const SERIES_ORDER = ['prestige', 'signature', 'legend', 'legend-select'];

/* Shell acrylics — base/vein hexes sampled from the factory's own swatch icons. */
const SHELLS = {
  platinum: { label: 'Platinum', base: '#a8a9a8', vein: '#8e9091', light: true },
  kalahari: { label: 'Kalahari', base: '#75726b', vein: '#57544d' },
  dakota: { label: 'Dakota', base: '#303232', vein: '#4d5352' },
  odyssey: { label: 'Odyssey', base: '#36363a', vein: '#56565c' },
  espresso: { label: 'Espresso', base: '#46382f', vein: '#2c211a' },
  'polar-white': { label: 'Polar White', base: '#f3f4f4', vein: '#dfe3e4', light: true },
};

/* Cabinets — Cedar/Graphite/Dusk Stone sampled; Sable + Grey Forever from brochure swatches. */
const CABINETS = {
  cedar: { label: 'Western Red Cedar', base: '#b36f47', accent: '#7e4a29', grain: 'wood' },
  sable: { label: 'Sable', base: '#352c25', accent: '#231d18', grain: 'composite' },
  'graphite-grey': { label: 'Graphite Grey', base: '#363333', accent: '#262424', grain: 'composite' },
  'grey-forever': { label: 'Grey Forever', base: '#736f69', accent: '#565350', grain: 'wood' },
  'dusk-stone': { label: 'Dusk Stone', base: '#42423f', accent: '#30302d', grain: 'composite' },
};

const SHELLS_BY_FAMILY = {
  core: ['platinum', 'dakota', 'kalahari', 'espresso'],
  classic: ['platinum', 'kalahari', 'dakota', 'odyssey', 'espresso', 'polar-white'],
  custom: ['platinum', 'kalahari', 'dakota', 'odyssey', 'espresso', 'polar-white'],
  awp: ['platinum'],
};
const CABINETS_BY_FAMILY = {
  core: ['dusk-stone'],
  classic: ['cedar', 'sable', 'graphite-grey'],
  custom: ['cedar', 'sable', 'graphite-grey', 'grey-forever'],
  awp: ['cedar', 'sable', 'graphite-grey'],
};

/**
 * Active lineup. L/W/H inches. seats null = factory doesn't quote a seat count
 * (dedicated swim trainers). series: jets/pumps per available Series.
 */
const MODELS = [
  /* ---- Custom Series ---- */
  { id: 'arctic-fox', name: 'Arctic Fox', family: 'custom', type: 'hottub', L: 85, W: 69, H: 39,
    sizeLabel: "7′ × 6′", seats: 4, lounger: 1, waterCap: '208 US gal (787 L)', dryWeight: '611 lb (277 kg)',
    series: { prestige: { jets: 15, pumps: 1 }, signature: { jets: 30, pumps: 2 } } },
  { id: 'cub', name: 'Cub', family: 'custom', type: 'hottub', L: 85, W: 85, H: 41,
    sizeLabel: "7′ × 7′", seats: 5, lounger: 1, waterCap: '275 US gal (1,041 L)', dryWeight: '741 lb (336 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 } } },
  { id: 'yukon', name: 'Yukon', family: 'custom', type: 'hottub', L: 85, W: 85, H: 41,
    sizeLabel: "7′ × 7′", seats: 6, lounger: 0, waterCap: '334 US gal (1,264 L)', dryWeight: '735 lb (333 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 } } },
  { id: 'summit', name: 'Summit', family: 'custom', type: 'hottub', L: 93, W: 93, H: 39,
    sizeLabel: "8′ × 8′", seats: 7, lounger: 0, waterCap: '360 US gal (1,363 L)', dryWeight: '935 lb (424 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 }, 'legend-select': { jets: 60, pumps: 3 } } },
  { id: 'tundra', name: 'Tundra', family: 'custom', type: 'hottub', L: 93, W: 93, H: 39,
    sizeLabel: "8′ × 8′", seats: 7, lounger: 0, waterCap: '411 US gal (1,556 L)', dryWeight: '951 lb (431 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 }, 'legend-select': { jets: 60, pumps: 3 } } },
  { id: 'kodiak', name: 'Kodiak', family: 'custom', type: 'hottub', L: 93, W: 93, H: 39,
    sizeLabel: "8′ × 8′", seats: 6, lounger: 1, waterCap: '342 US gal (1,295 L)', dryWeight: '973 lb (441 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 }, 'legend-select': { jets: 60, pumps: 3 } } },
  { id: 'klondiker', name: 'Klondiker', family: 'custom', type: 'hottub', L: 93, W: 93, H: 39,
    sizeLabel: "8′ × 8′", seats: 6, lounger: 2, waterCap: '373 US gal (1,412 L)', dryWeight: '944 lb (428 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 }, 'legend-select': { jets: 60, pumps: 3 } } },
  { id: 'summit-xl', name: 'Summit XL', family: 'custom', type: 'hottub', L: 117, W: 93, H: 39,
    sizeLabel: "10′ × 8′", seats: 9, lounger: 0, waterCap: '537 US gal (1,995 L)', dryWeight: '1,057 lb (479 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 }, 'legend-select': { jets: 60, pumps: 3 } } },

  /* ---- Classic Series ---- */
  { id: 'whistler', name: 'Whistler', family: 'classic', type: 'hottub', L: 85, W: 69, H: 39,
    sizeLabel: "7′ × 6′", seats: 3, lounger: 2, waterCap: '264 US gal (1,000 L)', dryWeight: '611 lb (277 kg)',
    series: { prestige: { jets: 15, pumps: 1 }, signature: { jets: 30, pumps: 2 } } },
  { id: 'timberwolf', name: 'Timberwolf', family: 'classic', type: 'hottub', L: 85, W: 69, H: 39,
    sizeLabel: "7′ × 6′", seats: 3, lounger: 2, waterCap: '234 US gal (884 L)', dryWeight: '611 lb (277 kg)',
    series: { prestige: { jets: 15, pumps: 1 }, signature: { jets: 30, pumps: 2 } } },
  { id: 'totem', name: 'Totem', family: 'classic', type: 'hottub', L: 85, W: 85, H: 41,
    sizeLabel: "7′ × 7′", seats: 7, lounger: 0, waterCap: '360 US gal (1,360 L)', dryWeight: '735 lb (333 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 } } },
  { id: 'eagle', name: 'Eagle', family: 'classic', type: 'hottub', L: 85, W: 85, H: 41,
    sizeLabel: "7′ × 7′", seats: 6, lounger: 1, waterCap: '313 US gal (1,185 L)', dryWeight: '741 lb (336 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 } } },
  { id: 'mckinley', name: 'McKinley', family: 'classic', type: 'hottub', L: 93, W: 93, H: 39,
    sizeLabel: "8′ × 8′", seats: 7, lounger: 0, waterCap: '449 US gal (1,700 L)', dryWeight: '951 lb (431 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 } } },
  { id: 'mustang', name: 'Mustang', family: 'classic', type: 'hottub', L: 93, W: 93, H: 39,
    sizeLabel: "8′ × 8′", seats: 6, lounger: 1, waterCap: '370 US gal (1,440 L)', dryWeight: '973 lb (441 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 } } },

  /* ---- Core Series ---- */
  { id: 'nova', name: 'Nova', family: 'core', type: 'hottub', L: 74, W: 84, H: 39,
    sizeLabel: "6′ × 7′", seats: 7, lounger: 0, waterCap: '272 US gal (1,030 L)', dryWeight: '858 lb (389 kg)',
    series: { prestige: { jets: 15, pumps: 1 }, signature: { jets: 30, pumps: 2 } } },
  { id: 'orion', name: 'Orion', family: 'core', type: 'hottub', L: 84, W: 84, H: 39,
    sizeLabel: "7′ × 7′", seats: 7, lounger: 0, waterCap: '400 US gal (1,514 L)', dryWeight: '1,001 lb (454 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 } } },
  { id: 'lunar', name: 'Lunar', family: 'core', type: 'hottub', L: 84, W: 84, H: 39,
    sizeLabel: "7′ × 7′", seats: 6, lounger: 1, waterCap: '375 US gal (1,420 L)', dryWeight: '1,001 lb (454 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 } } },

  /* ---- All-Weather Pools ---- */
  { id: 'ocean', name: 'Ocean', family: 'awp', type: 'swimspa', L: 172, W: 93, H: 51,
    sizeLabel: "14′ × 8′", seats: 12, lounger: 0, waterCap: '1,347 US gal (5,100 L)', dryWeight: '1,910 lb (866 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 }, 'legend-select': { jets: 60, pumps: 3 } } },
  { id: 'columbia', name: 'Columbia', family: 'awp', type: 'swimspa', L: 172, W: 93, H: 51,
    sizeLabel: "14′ × 8′", seats: 8, lounger: 0, waterCap: '1,347 US gal (5,100 L)', dryWeight: '1,910 lb (866 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 }, 'legend-select': { jets: 60, pumps: 3 } } },
  { id: 'okanagan', name: 'Okanagan', family: 'awp', type: 'swimspa', L: 172, W: 93, H: 51,
    sizeLabel: "14′ × 8′", seats: 12, lounger: 0, waterCap: '1,347 US gal (5,100 L)', dryWeight: '1,910 lb (866 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 } } },
  { id: 'athabascan', name: 'Athabascan', family: 'awp', type: 'swimspa', L: 172, W: 93, H: 51,
    sizeLabel: "14′ × 8′", seats: null, lounger: 0, waterCap: '1,347 US gal (5,100 L)', dryWeight: '1,910 lb (866 kg)',
    series: { prestige: { jets: 0, pumps: 1, jetsLabel: 'Dedicated swim lane' } } },
  { id: 'hudson', name: 'Hudson', family: 'awp', type: 'swimspa', L: 172, W: 93, H: 51,
    sizeLabel: "14′ × 8′", seats: null, lounger: 0, waterCap: '1,347 US gal (5,100 L)', dryWeight: '1,910 lb (866 kg)',
    series: { legend: { jets: 26, pumps: 3 } } },
  { id: 'kingfisher', name: 'Kingfisher', family: 'awp', type: 'swimspa', L: 172, W: 93, H: 51,
    sizeLabel: "14′ × 8′", seats: null, lounger: 0, waterCap: '1,347 US gal (5,100 L)', dryWeight: '1,910 lb (866 kg)',
    series: { signature: { jets: 3, pumps: 2, jetsLabel: '3 Niagara swim jets' } } },
  { id: 'wolverine', name: 'Wolverine', family: 'awp', type: 'swimspa', L: 172, W: 93, H: 51,
    sizeLabel: "14′ × 8′", seats: null, lounger: 0, waterCap: '1,347 US gal (5,100 L)', dryWeight: '1,910 lb (866 kg)',
    series: { legend: { jets: 26, pumps: 3 } } },
  { id: 'polar-bear', name: 'Polar Bear', family: 'awp', type: 'swimspa', L: 216, W: 93, H: 51,
    sizeLabel: "18′ × 8′", seats: 18, lounger: 2, waterCap: '1,901 US gal (7,200 L)', dryWeight: '2,265 lb (1,027 kg)',
    series: { prestige: { jets: 20, pumps: 1 }, signature: { jets: 40, pumps: 2 }, legend: { jets: 40, pumps: 3 } } },
];

const MODEL_BY_ID = {};
MODELS.forEach((m) => { MODEL_BY_ID[m.id] = m; });

function shellsFor(model) { return SHELLS_BY_FAMILY[model.family]; }
function cabinetsFor(model) { return CABINETS_BY_FAMILY[model.family]; }
function seriesFor(model) { return SERIES_ORDER.filter((s) => model.series[s]); }

/* ================================================================== *
 * TEXTURES — procedural canvases
 * ================================================================== */

function makeCanvas(size) {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  return c;
}

function rand(seedObj) {
  /* deterministic-enough LCG so rebuilds look identical */
  seedObj.s = (seedObj.s * 1664525 + 1013904223) % 4294967296;
  return seedObj.s / 4294967296;
}

/** Marbled acrylic albedo. */
function makeMarbleCanvas(base, vein) {
  const size = 512;
  const c = makeCanvas(size);
  const ctx = c.getContext('2d');
  const rng = { s: 42 };
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);
  /* soft cloudy blotches */
  for (let i = 0; i < 46; i++) {
    const x = rand(rng) * size, y = rand(rng) * size;
    const r = 30 + rand(rng) * 90;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, hexWithAlpha(vein, 0.16 + rand(rng) * 0.1));
    g.addColorStop(1, hexWithAlpha(vein, 0));
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  /* threads of vein */
  ctx.lineCap = 'round';
  for (let i = 0; i < 26; i++) {
    ctx.strokeStyle = hexWithAlpha(vein, 0.10 + rand(rng) * 0.12);
    ctx.lineWidth = 0.8 + rand(rng) * 1.8;
    ctx.beginPath();
    let x = rand(rng) * size, y = rand(rng) * size;
    ctx.moveTo(x, y);
    for (let k = 0; k < 4; k++) {
      const cx = x + (rand(rng) - 0.5) * 220, cy = y + (rand(rng) - 0.5) * 220;
      x += (rand(rng) - 0.5) * 260; y += (rand(rng) - 0.5) * 260;
      ctx.quadraticCurveTo(cx, cy, x, y);
    }
    ctx.stroke();
  }
  /* fine sparkle noise */
  for (let i = 0; i < 1400; i++) {
    const v = rand(rng);
    ctx.fillStyle = v > 0.5 ? 'rgba(255,255,255,0.030)' : 'rgba(0,0,0,0.030)';
    ctx.fillRect(rand(rng) * size, rand(rng) * size, 1.4, 1.4);
  }
  return c;
}

/** Vertical cabinet grain (wood = pronounced cedar grain, composite = subtle streaks). */
function makeGrainCanvas(base, accent, kind) {
  const size = 512;
  const c = makeCanvas(size);
  const ctx = c.getContext('2d');
  const rng = { s: 7 };
  const g = ctx.createLinearGradient(0, 0, 0, size);
  g.addColorStop(0, base);
  g.addColorStop(1, mixHex(base, accent, 0.35));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const lines = kind === 'wood' ? 130 : 90;
  for (let i = 0; i < lines; i++) {
    const x = rand(rng) * size;
    const w = kind === 'wood' ? 1 + rand(rng) * 3.5 : 0.8 + rand(rng) * 1.6;
    const alpha = kind === 'wood' ? 0.10 + rand(rng) * 0.16 : 0.05 + rand(rng) * 0.07;
    const tone = rand(rng) > 0.45 ? accent : '#000000';
    ctx.strokeStyle = hexWithAlpha(tone, alpha);
    ctx.lineWidth = w;
    ctx.beginPath();
    const wob = kind === 'wood' ? 9 : 2.5;
    ctx.moveTo(x + (rand(rng) - 0.5) * 4, 0);
    ctx.bezierCurveTo(x + (rand(rng) - 0.5) * wob, size * 0.33, x + (rand(rng) - 0.5) * wob, size * 0.66, x + (rand(rng) - 0.5) * 4, size);
    ctx.stroke();
  }
  if (kind === 'wood') {
    for (let i = 0; i < 5; i++) {
      const x = rand(rng) * size, y = rand(rng) * size;
      for (let r = 2; r < 14; r += 3) {
        ctx.strokeStyle = hexWithAlpha(accent, 0.20 - r * 0.012);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(x, y, r * 0.7, r * 1.6, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }
  return c;
}

/** Tileable pseudo normal map for the water surface. */
function makeWaterNormalCanvas() {
  const size = 256;
  const c = makeCanvas(size);
  const ctx = c.getContext('2d');
  const rng = { s: 99 };
  ctx.fillStyle = 'rgb(127,127,255)';
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 170; i++) {
    const x = rand(rng) * size, y = rand(rng) * size;
    const r = 8 + rand(rng) * 30;
    const dx = Math.round((rand(rng) - 0.5) * 56);
    const dy = Math.round((rand(rng) - 0.5) * 56);
    const col = `rgba(${127 + dx},${127 + dy},255,0.10)`;
    /* draw with wrapped copies so the texture tiles */
    [[0, 0], [size, 0], [-size, 0], [0, size], [0, -size]].forEach(([ox, oy]) => {
      const grd = ctx.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
      grd.addColorStop(0, col);
      grd.addColorStop(1, 'rgba(127,127,255,0)');
      ctx.fillStyle = grd;
      ctx.fillRect(x + ox - r, y + oy - r, r * 2, r * 2);
    });
  }
  return c;
}

/** Radial fade used as the ground disc alpha map. */
function makeGroundAlphaCanvas() {
  const size = 256;
  const c = makeCanvas(size);
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, '#ffffff');
  g.addColorStop(0.55, '#ffffff');
  g.addColorStop(1, '#000000');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return c;
}

function makeBackgroundCanvas(night) {
  const c = document.createElement('canvas');
  c.width = 2; c.height = 256;
  const ctx = c.getContext('2d');
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  if (night) {
    g.addColorStop(0, '#10141f');
    g.addColorStop(0.6, '#181f2c');
    g.addColorStop(1, '#0c0f16');
  } else {
    g.addColorStop(0, '#f2f5f7');
    g.addColorStop(0.6, '#dde4e8');
    g.addColorStop(1, '#c9d2d7');
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 2, 256);
  return c;
}

function hexWithAlpha(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function mixHex(h1, h2, t) {
  const a = parseInt(h1.slice(1), 16), b = parseInt(h2.slice(1), 16);
  const r = Math.round(((a >> 16) & 255) * (1 - t) + ((b >> 16) & 255) * t);
  const g = Math.round(((a >> 8) & 255) * (1 - t) + ((b >> 8) & 255) * t);
  const bl = Math.round((a & 255) * (1 - t) + (b & 255) * t);
  return `#${((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0')}`;
}

/* ================================================================== *
 * ENGINE
 * ================================================================== */

class SpaScene {
  /**
   * @param {object} THREE three module namespace
   * @param {HTMLCanvasElement} canvas
   */
  constructor(THREE, canvas) {
    this.T = THREE;
    this.canvas = canvas;
    this.textures = {};
    this.night = false;
    this.coverOn = false;
    this.active = true;
    this.lastInteract = performance.now();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    this.renderer = renderer;

    this.scene = new THREE.Scene();
    this.bgDay = new THREE.CanvasTexture(makeBackgroundCanvas(false));
    this.bgNight = new THREE.CanvasTexture(makeBackgroundCanvas(true));
    this.bgDay.colorSpace = THREE.SRGBColorSpace;
    this.bgNight.colorSpace = THREE.SRGBColorSpace;
    this.scene.background = this.bgDay;

    this.camera = new THREE.PerspectiveCamera(38, 1, 1, 4000);

    /* orbit state (degrees-free spherical w/ damping) */
    this.orbit = {
      theta: -0.65, phi: 1.05, radius: 260,
      tTheta: -0.65, tPhi: 1.05, tRadius: 260,
      min: 120, max: 700,
      target: new THREE.Vector3(0, 22, 0),
    };

    /* studio environment via PMREM (hand-built — no examples/jsm import) */
    const pmrem = new THREE.PMREMGenerator(renderer);
    /* note: fromScene's internal cube camera defaults to far=100 — our studio
       room is 1200 units, so pass explicit near/far or everything clips to black */
    this.scene.environment = pmrem.fromScene(this._studioEnvScene(), 0.04, 1, 2400).texture;
    pmrem.dispose();

    /* lights */
    this.hemi = new THREE.HemisphereLight(0xdfe8ee, 0x4c5258, 0.55);
    this.scene.add(this.hemi);
    this.key = new THREE.DirectionalLight(0xfff2e2, 2.6);
    this.key.position.set(180, 300, 200);
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(2048, 2048);
    this.key.shadow.camera.left = -280; this.key.shadow.camera.right = 280;
    this.key.shadow.camera.top = 280; this.key.shadow.camera.bottom = -280;
    this.key.shadow.camera.near = 50; this.key.shadow.camera.far = 900;
    this.key.shadow.bias = -0.0004;
    this.scene.add(this.key);

    /* night accents (off by day) */
    this.waterGlow = new THREE.PointLight(0x4fd7ff, 0, 320, 2);
    this.scene.add(this.waterGlow);
    this.underGlowA = new THREE.PointLight(0x57c8ff, 0, 260, 2);
    this.underGlowB = new THREE.PointLight(0x57c8ff, 0, 260, 2);
    this.scene.add(this.underGlowA, this.underGlowB);

    /* ground */
    const groundGeo = new THREE.CircleGeometry(640, 48);
    const alpha = new THREE.CanvasTexture(makeGroundAlphaCanvas());
    this.groundMat = new THREE.MeshStandardMaterial({
      color: 0xd9dde0, roughness: 0.95, metalness: 0,
      transparent: true, alphaMap: alpha, depthWrite: false,
    });
    const ground = new THREE.Mesh(groundGeo, this.groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    /* shared materials (textures swapped on colour change) */
    this.acrylicMat = new THREE.MeshPhysicalMaterial({
      roughness: 0.16, metalness: 0, clearcoat: 1, clearcoatRoughness: 0.15,
      envMapIntensity: 1.15,
    });
    this.cabinetMat = new THREE.MeshStandardMaterial({ roughness: 0.78, metalness: 0 });
    this.darkMat = new THREE.MeshStandardMaterial({ color: 0x191a1c, roughness: 0.9 });
    this.chromeMat = new THREE.MeshStandardMaterial({ color: 0xd8dcdf, metalness: 1, roughness: 0.28 });
    this.pillowMat = new THREE.MeshStandardMaterial({ color: 0x1f2125, roughness: 0.6 });
    this.coverMat = new THREE.MeshStandardMaterial({ color: 0x2c2e31, roughness: 0.88 });
    this.ledMat = new THREE.MeshStandardMaterial({ color: 0x0c2a33, emissive: 0x39dcff, emissiveIntensity: 0.25 });

    const waterNormal = new THREE.CanvasTexture(makeWaterNormalCanvas());
    waterNormal.wrapS = waterNormal.wrapT = THREE.RepeatWrapping;
    this.waterNormal = waterNormal;
    this.waterMat = new THREE.MeshPhysicalMaterial({
      color: 0x6fd0e6, transparent: true, opacity: 0.55,
      roughness: 0.1, metalness: 0, clearcoat: 0.7, clearcoatRoughness: 0.1,
      normalMap: waterNormal, normalScale: new THREE.Vector2(0.42, 0.42),
      envMapIntensity: 1.35,
      emissive: 0x0e5d74, emissiveIntensity: 0.45,
    });

    this.spaGroup = null;
    this._bindControls();

    this._clock = new THREE.Clock();
    renderer.setAnimationLoop(() => this._tick());
  }

  /* small emissive-panel room → PMREM (studio reflections) */
  _studioEnvScene() {
    const T = this.T;
    const s = new T.Scene();
    const room = new T.Mesh(
      new T.BoxGeometry(1200, 800, 1200),
      new T.MeshBasicMaterial({ color: 0x687077, side: T.BackSide })
    );
    room.position.y = 300;
    s.add(room);
    const panel = (w, h, color, intensity, x, y, z, ry, rx) => {
      const m = new T.Mesh(new T.PlaneGeometry(w, h), new T.MeshBasicMaterial({ color }));
      m.material.color.multiplyScalar(intensity);
      m.position.set(x, y, z);
      if (ry) m.rotation.y = ry;
      if (rx) m.rotation.x = rx;
      s.add(m);
    };
    panel(700, 700, 0xffffff, 5.0, 0, 690, 0, 0, Math.PI / 2);   /* big top softbox */
    panel(900, 420, 0xcfe2ee, 2.2, -590, 340, 0, Math.PI / 2, 0); /* cool side strip */
    panel(900, 340, 0xffeedd, 1.8, 590, 300, 0, -Math.PI / 2, 0); /* warm side strip */
    panel(700, 320, 0xffffff, 1.4, 0, 300, -590, 0, 0);           /* back fill */
    panel(700, 320, 0xeef3f6, 1.1, 0, 300, 590, Math.PI, 0);      /* front fill */
    return s;
  }

  /* ---------- procedural spa ---------- */

  /** Full rebuild for a model (colours applied via shared materials). */
  buildSpa(model) {
    const T = this.T;
    if (this.spaGroup) {
      this.scene.remove(this.spaGroup);
      this.spaGroup.traverse((o) => { if (o.geometry) o.geometry.dispose(); });
    }
    const g = new T.Group();
    this.spaGroup = g;
    this.model = model;

    const L = model.L, W = model.W, H = model.H;
    const R = 11;                  /* corner radius */
    const lipDrop = 4.5;           /* acrylic band height at top of cabinet */
    const rimW = model.type === 'swimspa' ? 8 : 9.5; /* rim deck width */
    const bowlL = L - rimW * 2, bowlW = W - rimW * 2, bowlR = Math.max(5, R - rimW * 0.55);

    /* --- cabinet backing (thin perimeter shell behind the slats) --- */
    const backing = this._extrude(
      ringShape(T, L - 2.4, W - 2.4, R - 1, L - 7.4, W - 7.4, Math.max(3, R - 3.5)),
      H - lipDrop - 1, this.darkMat
    );
    backing.position.y = 0.6;
    g.add(backing);

    /* --- base skirt --- */
    const skirt = this._extrude(roundedRect(T, L - 0.6, W - 0.6, R), 2.6, this.darkMat);
    skirt.position.y = 0;
    g.add(skirt);

    /* --- cabinet slats (instanced) --- */
    const slatH = H - lipDrop - 4.2;
    const slatGeo = new T.BoxGeometry(6.1, slatH, 1.1);
    const pts = roundedRect(T, L, W, R).getSpacedPoints(Math.round(perimeter(L, W, R) / 5.9));
    const slats = new T.InstancedMesh(slatGeo, this.cabinetMat, pts.length - 1);
    const m4 = new T.Matrix4(), e = new T.Euler(), q = new T.Quaternion(), v = new T.Vector3(), one = new T.Vector3(1, 1, 1);
    for (let i = 0; i < pts.length - 1; i++) {
      const p = pts[i];
      const pPrev = pts[(i - 1 + pts.length - 1) % (pts.length - 1)];
      const pNext = pts[(i + 1) % (pts.length - 1)];
      const tx = pNext.x - pPrev.x, ty = pNext.y - pPrev.y;
      /* outward normal of CCW path */
      let nx = ty, ny = -tx;
      if (nx * p.x + ny * p.y < 0) { nx = -nx; ny = -ny; }
      const yaw = Math.atan2(nx, ny);
      e.set(0, yaw, 0);
      q.setFromEuler(e);
      v.set(p.x, 3.4 + slatH / 2, p.y);
      m4.compose(v, q, one);
      slats.setMatrixAt(i, m4);
    }
    slats.castShadow = true;
    g.add(slats);

    /* --- acrylic lip band + rim deck --- */
    const lip = this._extrude(ringShape(T, L + 4.6, W + 4.6, R + 2.3, L - 3, W - 3, R - 1.5), lipDrop, this.acrylicMat, 0.9);
    lip.position.y = H - lipDrop - 0.9;
    lip.castShadow = true;
    g.add(lip);

    const rim = this._extrude(ringShape(T, L + 4.6, W + 4.6, R + 2.3, bowlL, bowlW, bowlR), 1.6, this.acrylicMat, 0.8);
    rim.position.y = H - 1.6 - 0.8;
    rim.castShadow = true;
    g.add(rim);

    /* --- bowl: upper wall, bench, lower wall, floor --- */
    const benchDrop = 16;          /* rim to seat top */
    const benchW = model.type === 'swimspa' ? 0 : 15;
    const floorDrop = model.type === 'swimspa' ? 42 : 29;

    const upperWall = this._extrude(ringShape(T, bowlL, bowlW, bowlR, bowlL - 3, bowlW - 3, bowlR - 1.5), benchDrop, this.acrylicMat);
    upperWall.position.y = H - 1.2 - benchDrop;
    g.add(upperWall);

    if (model.type === 'hottub') {
      /* continuous barrier-free bench around the bowl (Arctic Spas signature) */
      const bench = this._extrude(
        ringShape(T, bowlL - 1.5, bowlW - 1.5, bowlR - 1, bowlL - benchW * 2, bowlW - benchW * 2, Math.max(3, bowlR - benchW * 0.7)),
        3.4, this.acrylicMat, 1.1
      );
      bench.position.y = H - benchDrop - 3.4;
      g.add(bench);

      const lowerWall = this._extrude(
        ringShape(T, bowlL - benchW * 2 + 2, bowlW - benchW * 2 + 2, Math.max(3, bowlR - benchW * 0.7), bowlL - benchW * 2 - 1, bowlW - benchW * 2 - 1, Math.max(2.5, bowlR - benchW * 0.7 - 1)),
        floorDrop - benchDrop - 2, this.acrylicMat
      );
      lowerWall.position.y = H - floorDrop;
      g.add(lowerWall);
    } else {
      /* swim spa: open tank wall straight to floor */
      const tankWall = this._extrude(ringShape(T, bowlL, bowlW, bowlR, bowlL - 3, bowlW - 3, bowlR - 1.5), floorDrop - benchDrop, this.acrylicMat);
      tankWall.position.y = H - 1.2 - floorDrop;
      g.add(tankWall);
    }

    const floor = this._extrude(roundedRect(T, bowlL - 2, bowlW - 2, bowlR), 1.4, this.acrylicMat);
    floor.position.y = H - floorDrop - 1.4;
    g.add(floor);

    /* --- swim spa furniture: therapy bench at -X end, swim jets at +X --- */
    if (model.type === 'swimspa') {
      const bench = this._box(26, 4, bowlW - 6, this.acrylicMat);
      bench.position.set(-bowlL / 2 + 14, H - benchDrop - 2, 0);
      g.add(bench);
      const benchBack = this._box(6, benchDrop - 4, bowlW - 6, this.acrylicMat);
      benchBack.position.set(-bowlL / 2 + 4, H - (benchDrop - 4) / 2 - 3, 0);
      g.add(benchBack);
      /* lane stripe */
      const stripe = this._box(bowlL - 70, 0.6, 8, this.darkMat);
      stripe.position.set(14, H - floorDrop + 0.4, 0);
      g.add(stripe);
      /* swim jet trio */
      for (let i = -1; i <= 1; i++) {
        const sj = new T.Mesh(new T.TorusGeometry(3.1, 0.9, 12, 28), this.chromeMat);
        sj.rotation.y = Math.PI / 2;
        sj.position.set(bowlL / 2 - 2.4, H - 17, i * 16);
        g.add(sj);
      }
    }

    /* --- lounger(s) --- */
    /* lounger = reclined slab leaning against a side wall (±Z) */
    const loungers = model.type === 'hottub' ? (model.lounger || 0) : 0;
    for (let i = 0; i < Math.min(loungers, 2); i++) {
      const side = i === 0 ? 1 : -1;
      const slab = this._box(30, 3.4, 30, this.acrylicMat);
      slab.rotation.x = side * 0.42; /* head high at the wall, feet toward footwell */
      slab.position.set(-side * bowlL * 0.16, H - benchDrop - 2.5, side * (bowlW / 2 - 17));
      g.add(slab);
    }

    /* --- pillows --- */
    const pillowCount = model.type === 'swimspa' ? (model.seats ? 3 : 2) : Math.max(2, Math.min(model.seats || 4, 6)) - (loungers ? 0 : 0);
    const pPos = pillowPositions(model, bowlL, bowlW, pillowCount, loungers);
    pPos.forEach((pp) => {
      const pil = this._box(11, 4.4, 2.6, this.pillowMat, 1.0);
      pil.position.set(pp.x, H - 4.6, pp.z);
      pil.rotation.y = pp.yaw;
      g.add(pil);
    });

    /* --- waterline LED band --- */
    const ledCurve = new RoundedRectCurve(bowlL - 2.5, bowlW - 2.5, bowlR);
    const led = new T.Mesh(new T.TubeGeometry(ledCurve, 90, 0.55, 8, true), this.ledMat);
    led.position.y = H - 8.6;
    g.add(led);
    this.ledMesh = led;

    /* --- water --- */
    const waterShape = roundedRect(T, bowlL - 3.5, bowlW - 3.5, Math.max(3, bowlR - 1));
    const water = new T.Mesh(new T.ShapeGeometry(waterShape, 24), this.waterMat);
    water.rotation.x = -Math.PI / 2;
    water.position.y = H - 8.2;
    this.waterMesh = water;
    const span = Math.max(bowlL, bowlW);
    this.waterNormal.repeat.set(span / 90, span / 90);
    g.add(water);

    /* --- topside control puck --- */
    const puck = new T.Mesh(new T.CylinderGeometry(3.4, 3.8, 1.3, 24), this.darkMat);
    puck.position.set(bowlL / 2 + rimW / 2 - 1, H + 0.4, bowlW / 2 + rimW / 2 - 1);
    /* place on the rim corner diagonal */
    puck.position.x = (L / 2 - rimW * 0.45) * 0.72;
    puck.position.z = (W / 2 - rimW * 0.45) * 0.72;
    g.add(puck);
    const screen = this._box(3.4, 0.3, 2.2, this.ledMat);
    screen.position.set(puck.position.x, H + 1.1, puck.position.z);
    g.add(screen);

    /* --- jets (visible count scales with Series) --- */
    this.jetAnchor = { bowlL, bowlW, bowlR, H, benchDrop };
    this.jetsMesh = null;

    /* --- cover --- */
    const cg = new T.Group();
    const halfL = (L + 5) / 2 - 0.8;
    [-1, 1].forEach((side) => {
      const half = this._extrude(roundedRect(T, halfL, W + 5, R + 2), 4.0, this.coverMat, 1.4);
      half.position.set(side * (halfL / 2 + 0.8), H + 0.4, 0);
      half.castShadow = true;
      cg.add(half);
    });
    const hinge = this._box(3.5, 1.2, W + 5.5, this.darkMat);
    hinge.position.y = H + 5.4;
    cg.add(hinge);
    [-1, 1].forEach((sx) => [-1, 1].forEach((sz) => {
      const strap = this._box(3.2, 6.5, 0.9, this.darkMat);
      strap.position.set(sx * L * 0.3, H - 3.2, sz * (W / 2 + 2.6));
      cg.add(strap);
    }));
    cg.visible = this.coverOn;
    this.coverGroup = cg;
    g.add(cg);

    g.traverse((o) => { if (o.isMesh) { o.castShadow = o.castShadow || false; o.receiveShadow = true; } });
    lip.castShadow = rim.castShadow = slats.castShadow = true;

    this.scene.add(g);

    /* lights anchored to tub */
    this.waterGlow.position.set(0, H - 14, 0);
    this.underGlowA.position.set(L * 0.45, 6, W * 0.45);
    this.underGlowB.position.set(-L * 0.45, 6, -W * 0.45);

    this.fitCamera(model);
  }

  /** Rebuild only the jet ring for the active Series. */
  setJets(count) {
    const T = this.T;
    if (this.jetsMesh) {
      this.spaGroup.remove(this.jetsMesh);
      this.jetsMesh.geometry.dispose();
      this.jetsMesh = null;
    }
    const visible = Math.max(0, Math.min(count, 24));
    if (!visible || !this.jetAnchor) return;
    const { bowlL, bowlW, bowlR, H, benchDrop } = this.jetAnchor;
    const geo = new T.TorusGeometry(1.45, 0.42, 10, 22);
    const mesh = new T.InstancedMesh(geo, this.chromeMat, visible);
    const curve = new RoundedRectCurve(bowlL - 2.2, bowlW - 2.2, bowlR);
    const m4 = new T.Matrix4(), q = new T.Quaternion(), e = new T.Euler(), v = new T.Vector3(), one = new T.Vector3(1, 1, 1);
    const p = new T.Vector3(), tan = new T.Vector3();
    for (let i = 0; i < visible; i++) {
      const t = (i + 0.5) / visible;
      curve.getPoint(t, p);
      curve.getTangent(t, tan);
      const yaw = Math.atan2(-tan.z, tan.x) + Math.PI / 2;
      e.set(0, yaw, 0);
      q.setFromEuler(e);
      const y = H - benchDrop + 3.5 + (i % 2 ? 2.6 : -1.2);
      v.set(p.x, y, p.z);
      m4.compose(v, q, one);
      mesh.setMatrixAt(i, m4);
    }
    this.jetsMesh = mesh;
    this.spaGroup.add(mesh);
  }

  setShell(shellId) {
    const tex = this._texture(`shell:${shellId}`, () => makeMarbleCanvas(SHELLS[shellId].base, SHELLS[shellId].vein));
    this.acrylicMat.map = tex;
    this.acrylicMat.needsUpdate = true;
  }

  setCabinet(cabId) {
    const c = CABINETS[cabId];
    const tex = this._texture(`cab:${cabId}`, () => makeGrainCanvas(c.base, c.accent, c.grain));
    this.cabinetMat.map = tex;
    this.cabinetMat.needsUpdate = true;
  }

  setCover(on) {
    this.coverOn = on;
    if (this.coverGroup) this.coverGroup.visible = on;
  }

  setNight(on) {
    this.night = on;
    this.scene.background = on ? this.bgNight : this.bgDay;
    this.scene.environmentIntensity = on ? 0.18 : 1.0;
    this.hemi.intensity = on ? 0.1 : 0.55;
    this.key.intensity = on ? 0.3 : 2.6;
    this.key.color.set(on ? 0xa9c6ff : 0xfff2e2);
    this.groundMat.color.set(on ? 0x171b20 : 0xd9dde0);
    this.ledMat.emissiveIntensity = on ? 3.4 : 0.25;
    this.waterGlow.intensity = on ? 5200 : 0;
    this.underGlowA.intensity = on ? 1500 : 0;
    this.underGlowB.intensity = on ? 1500 : 0;
    this.waterMat.emissive.set(on ? 0x10708e : 0x0e5d74);
    this.waterMat.emissiveIntensity = on ? 0.9 : 0.45;
  }

  fitCamera(model) {
    const diag = Math.hypot(model.L, model.W);
    const r = diag * 1.62 + 40;
    this.orbit.tRadius = r;
    this.orbit.radius = r;
    this.orbit.min = diag * 0.85;
    this.orbit.max = diag * 3.2;
    this.orbit.target.set(0, model.H * 0.52, 0);
    this.orbit.tTheta = -0.65;
    this.orbit.tPhi = 1.02;
  }

  resize(w, h) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setSize(w, h, false);
    this.renderer.setPixelRatio(dpr);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.spaGroup) this.renderNow();
  }

  /* ---------- internals ---------- */

  _texture(key, makeCanvasFn) {
    if (!this.textures[key]) {
      const t = new this.T.CanvasTexture(makeCanvasFn());
      t.colorSpace = this.T.SRGBColorSpace;
      t.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      this.textures[key] = t;
    }
    return this.textures[key];
  }

  _extrude(shape, depth, mat, bevel) {
    const T = this.T;
    const geo = new T.ExtrudeGeometry(shape, {
      depth, bevelEnabled: !!bevel, bevelThickness: bevel || 0, bevelSize: bevel || 0,
      bevelSegments: 2, curveSegments: 20,
    });
    const mesh = new T.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2; /* shape XY → world XZ, extrude → +Y */
    return mesh;
  }

  _box(w, h, d, mat) {
    return new this.T.Mesh(new this.T.BoxGeometry(w, h, d), mat);
  }

  _bindControls() {
    const c = this.canvas;
    const o = this.orbit;
    const pointers = new Map();
    let lastPinch = 0;

    const mark = () => { this.lastInteract = performance.now(); };

    c.style.touchAction = 'none';
    c.addEventListener('pointerdown', (ev) => {
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      c.setPointerCapture(ev.pointerId);
      mark();
    });
    c.addEventListener('pointermove', (ev) => {
      if (!pointers.has(ev.pointerId)) return;
      const prev = pointers.get(ev.pointerId);
      const dx = ev.clientX - prev.x, dy = ev.clientY - prev.y;
      pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (pointers.size === 1) {
        o.tTheta -= dx * 0.0062;
        o.tPhi = clamp(o.tPhi - dy * 0.005, 0.32, 1.42);
        mark();
      } else if (pointers.size === 2) {
        const pts = [...pointers.values()];
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (lastPinch) o.tRadius = clamp(o.tRadius * (lastPinch / dist), o.min, o.max);
        lastPinch = dist;
        mark();
      }
    });
    const up = (ev) => { pointers.delete(ev.pointerId); if (pointers.size < 2) lastPinch = 0; };
    c.addEventListener('pointerup', up);
    c.addEventListener('pointercancel', up);
    c.addEventListener('wheel', (ev) => {
      ev.preventDefault();
      o.tRadius = clamp(o.tRadius * (1 + Math.sign(ev.deltaY) * 0.09), o.min, o.max);
      mark();
    }, { passive: false });
    c.addEventListener('dblclick', () => { this.fitCamera(this.model); mark(); });
  }

  /** One synchronous frame — used after state changes so the canvas is always
      current even where requestAnimationFrame is throttled (hidden tabs,
      headless screenshots). */
  renderNow() {
    const o = this.orbit;
    o.theta = o.tTheta; o.phi = o.tPhi; o.radius = o.tRadius;
    const sp = Math.sin(o.phi), cp = Math.cos(o.phi);
    this.camera.position.set(
      o.target.x + o.radius * sp * Math.sin(o.theta),
      o.target.y + o.radius * cp,
      o.target.z + o.radius * sp * Math.cos(o.theta)
    );
    this.camera.lookAt(o.target);
    this.renderer.render(this.scene, this.camera);
  }

  _tick() {
    if (!this.active || document.hidden) return;
    const dt = Math.min(this._clock.getDelta(), 0.05);
    const o = this.orbit;

    if (!this.reducedMotion && performance.now() - this.lastInteract > 5000) {
      o.tTheta += dt * 0.10; /* idle auto-spin */
    }
    const k = this.reducedMotion ? 1 : Math.min(1, dt * 7);
    o.theta += (o.tTheta - o.theta) * k;
    o.phi += (o.tPhi - o.phi) * k;
    o.radius += (o.tRadius - o.radius) * k;

    const sp = Math.sin(o.phi), cp = Math.cos(o.phi);
    this.camera.position.set(
      o.target.x + o.radius * sp * Math.sin(o.theta),
      o.target.y + o.radius * cp,
      o.target.z + o.radius * sp * Math.cos(o.theta)
    );
    this.camera.lookAt(o.target);

    if (!this.reducedMotion) {
      this.waterNormal.offset.x += dt * 0.018;
      this.waterNormal.offset.y += dt * 0.011;
    }
    this.renderer.render(this.scene, this.camera);
  }
}

/* rounded-rect helpers */
function roundedRect(T, w, h, r) {
  const s = new T.Shape();
  addRoundedRectPath(s, w, h, Math.min(r, w / 2 - 0.1, h / 2 - 0.1));
  return s;
}
function ringShape(T, ow, oh, or_, iw, ih, ir) {
  const s = roundedRect(T, ow, oh, or_);
  const hole = new T.Path();
  addRoundedRectPath(hole, iw, ih, Math.min(ir, iw / 2 - 0.1, ih / 2 - 0.1));
  s.holes.push(hole);
  return s;
}
function addRoundedRectPath(p, w, h, r) {
  const x = -w / 2, y = -h / 2;
  p.moveTo(x + r, y);
  p.lineTo(x + w - r, y);
  p.absarc(x + w - r, y + r, r, -Math.PI / 2, 0, false);
  p.lineTo(x + w, y + h - r);
  p.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2, false);
  p.lineTo(x + r, y + h);
  p.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false);
  p.lineTo(x, y + r);
  p.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
}
function perimeter(w, h, r) {
  return 2 * (w - 2 * r) + 2 * (h - 2 * r) + 2 * Math.PI * r;
}
function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

/** 3D closed curve along a rounded rect in the XZ plane (for tubes/jet placement). */
function makeRoundedRectCurveClass(T) {
  class C extends T.Curve {
    constructor(w, h, r) {
      super();
      this.shape = new T.Shape();
      addRoundedRectPath(this.shape, w, h, Math.min(r, w / 2 - 0.1, h / 2 - 0.1));
      this.pts = this.shape.getSpacedPoints(200);
    }
    getPoint(t, target) {
      const out = target || new T.Vector3();
      const i = Math.min(Math.floor(t * (this.pts.length - 1)), this.pts.length - 2);
      const f = t * (this.pts.length - 1) - i;
      const a = this.pts[i], b = this.pts[i + 1];
      return out.set(a.x + (b.x - a.x) * f, 0, a.y + (b.y - a.y) * f);
    }
  }
  return C;
}
let RoundedRectCurve = null; /* assigned once THREE loads */

/* ================================================================== *
 * UI
 * ================================================================== */

const DEFAULTS = { model: 'summit', series: 'signature', shell: 'platinum', cabinet: 'cedar' };

class BuilderUI {
  /**
   * @param {HTMLElement} root section root ([data-sb3-root])
   * @param {SpaScene} engine
   */
  constructor(root, engine, isPrimary) {
    this.root = root;
    this.engine = engine;
    this.isPrimary = isPrimary; /* primary instance owns URL state */

    const fromURL = this._readURL();
    const def = root.dataset.defaultModel && MODEL_BY_ID[root.dataset.defaultModel] ? root.dataset.defaultModel : DEFAULTS.model;
    this.state = {
      model: fromURL.model || def,
      series: fromURL.series || DEFAULTS.series,
      shell: fromURL.shell || DEFAULTS.shell,
      cabinet: fromURL.cabinet || DEFAULTS.cabinet,
      cover: false,
      night: false,
    };
    this._clampState();

    this.controls = root.querySelector('[data-sb3-controls]');
    this._buildControls();
    this._buildStageChips();
    this.applyAll();
  }

  get modelObj() { return MODEL_BY_ID[this.state.model]; }

  _clampState() {
    const m = this.modelObj || MODEL_BY_ID[DEFAULTS.model];
    this.state.model = m.id;
    if (!m.series[this.state.series]) this.state.series = seriesFor(m)[seriesFor(m).length - 1];
    if (!shellsFor(m).includes(this.state.shell)) this.state.shell = shellsFor(m)[0];
    if (!cabinetsFor(m).includes(this.state.cabinet)) this.state.cabinet = cabinetsFor(m)[0];
  }

  /* ----- DOM builders ----- */

  _group(label) {
    const wrap = document.createElement('div');
    wrap.className = 'sb3-group';
    const lab = document.createElement('span');
    lab.className = 'sb3-group-label';
    lab.textContent = label;
    wrap.appendChild(lab);
    this.controls.appendChild(wrap);
    return wrap;
  }

  _buildControls() {
    /* family tabs */
    const famWrap = this._group('Model');
    const tabs = document.createElement('div');
    tabs.className = 'sb3-tabs';
    tabs.setAttribute('role', 'tablist');
    Object.keys(FAMILIES).forEach((fid) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sb3-tab';
      b.dataset.family = fid;
      b.textContent = FAMILIES[fid].label;
      b.addEventListener('click', () => {
        this.activeFamily = fid;
        const first = MODELS.find((m) => m.family === fid);
        this.update({ model: first.id });
      });
      tabs.appendChild(b);
    });
    famWrap.appendChild(tabs);
    this.tabsEl = tabs;

    const grid = document.createElement('div');
    grid.className = 'sb3-models';
    famWrap.appendChild(grid);
    this.modelsEl = grid;

    /* series */
    const serWrap = this._group('Series');
    const seg = document.createElement('div');
    seg.className = 'sb3-seg';
    seg.setAttribute('role', 'group');
    seg.setAttribute('aria-label', 'Series');
    serWrap.appendChild(seg);
    this.seriesEl = seg;
    const cap = document.createElement('p');
    cap.className = 'sb3-series-cap';
    serWrap.appendChild(cap);
    this.seriesCapEl = cap;

    /* shells */
    const shWrap = this._group('Shell Colour');
    const shRow = document.createElement('div');
    shRow.className = 'sb3-swatches';
    shRow.setAttribute('role', 'group');
    shRow.setAttribute('aria-label', 'Shell colour');
    shWrap.appendChild(shRow);
    this.shellsEl = shRow;

    /* cabinets */
    const cabWrap = this._group('Cabinet Colour');
    const cabRow = document.createElement('div');
    cabRow.className = 'sb3-swatches';
    cabRow.setAttribute('role', 'group');
    cabRow.setAttribute('aria-label', 'Cabinet colour');
    cabWrap.appendChild(cabRow);
    this.cabinetsEl = cabRow;

    /* specs */
    const specWrap = this._group('Your Build');
    const spec = document.createElement('div');
    spec.className = 'sb3-specs';
    specWrap.appendChild(spec);
    this.specsEl = spec;
    const badges = document.createElement('div');
    badges.className = 'sb3-badges';
    specWrap.appendChild(badges);
    this.badgesEl = badges;
  }

  _buildStageChips() {
    const row = this.root.querySelector('[data-sb3-chips]');
    if (!row) return;
    const mk = (label, key) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sb3-chip';
      b.setAttribute('aria-pressed', 'false');
      b.textContent = label;
      b.addEventListener('click', () => {
        const patch = {};
        patch[key] = !this.state[key];
        this.update(patch);
      });
      row.appendChild(b);
      return b;
    };
    this.coverChip = mk('Cover on', 'cover');
    this.nightChip = mk('Night mode', 'night');
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'sb3-chip';
    reset.textContent = 'Reset view';
    reset.addEventListener('click', () => { this.engine.fitCamera(this.modelObj); this.engine.renderNow(); });
    row.appendChild(reset);
  }

  /* ----- state flow ----- */

  update(patch) {
    const prevModel = this.state.model;
    Object.assign(this.state, patch);
    this._clampState();
    if (this.state.model !== prevModel || patch.model) {
      this.engine.buildSpa(this.modelObj);
      this.engine.setCover(this.state.cover);
    }
    this.applyAll(patch.model ? false : true);
  }

  applyAll(skipBuild) {
    if (!skipBuild && !this.engine.spaGroup) {
      this.engine.buildSpa(this.modelObj);
    }
    const m = this.modelObj;
    const ser = m.series[this.state.series];
    this.engine.setShell(this.state.shell);
    this.engine.setCabinet(this.state.cabinet);
    this.engine.setJets(ser.jets);
    this.engine.setCover(this.state.cover);
    this.engine.setNight(this.state.night);
    this.renderUI();
    this.syncForm();
    if (this.isPrimary) this._writeURL();
    this.engine.renderNow();
  }

  renderUI() {
    const m = this.modelObj;
    this.activeFamily = m.family;

    /* tabs */
    [...this.tabsEl.children].forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.family === this.activeFamily));
    });

    /* model chips for active family */
    this.modelsEl.innerHTML = '';
    MODELS.filter((x) => x.family === this.activeFamily).forEach((x) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sb3-model';
      b.setAttribute('aria-pressed', String(x.id === m.id));
      const seatsBit = x.seats ? ` · seats ${x.seats}` : '';
      b.innerHTML = `<strong>${x.name}</strong><span>${x.sizeLabel}${seatsBit}</span>`;
      b.addEventListener('click', () => this.update({ model: x.id }));
      this.modelsEl.appendChild(b);
    });

    /* series segmented */
    this.seriesEl.innerHTML = '';
    seriesFor(m).forEach((sid) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sb3-seg-btn';
      b.textContent = SERIES[sid].label;
      b.setAttribute('aria-pressed', String(sid === this.state.series));
      b.addEventListener('click', () => this.update({ series: sid }));
      this.seriesEl.appendChild(b);
    });
    const ser = m.series[this.state.series];
    this.seriesCapEl.textContent = ser.jetsLabel || `${ser.jets} jets · ${ser.pumps} pump${ser.pumps > 1 ? 's' : ''}`;

    /* shell swatches */
    this.shellsEl.innerHTML = '';
    shellsFor(m).forEach((sid) => {
      const s = SHELLS[sid];
      const b = swatchButton(s.label, `radial-gradient(circle at 32% 30%, ${s.vein} 0%, transparent 55%), radial-gradient(circle at 70% 75%, ${s.vein} 0%, transparent 45%), ${s.base}`);
      b.setAttribute('aria-pressed', String(sid === this.state.shell));
      b.addEventListener('click', () => this.update({ shell: sid }));
      this.shellsEl.appendChild(b);
    });

    /* cabinet swatches */
    this.cabinetsEl.innerHTML = '';
    cabinetsFor(m).forEach((cid) => {
      const cdef = CABINETS[cid];
      const b = swatchButton(cdef.label, `repeating-linear-gradient(90deg, rgba(0,0,0,0.20) 0 1.5px, transparent 1.5px 6px), linear-gradient(180deg, ${cdef.base}, ${cdef.accent})`);
      b.setAttribute('aria-pressed', String(cid === this.state.cabinet));
      b.addEventListener('click', () => this.update({ cabinet: cid }));
      this.cabinetsEl.appendChild(b);
    });

    /* chips */
    if (this.coverChip) this.coverChip.setAttribute('aria-pressed', String(this.state.cover));
    if (this.nightChip) this.nightChip.setAttribute('aria-pressed', String(this.state.night));

    /* specs */
    const rows = [
      ['Model', `${m.name} — ${FAMILIES[m.family].label}`],
      ['Footprint', `${m.sizeLabel} (${m.L}″ × ${m.W}″ × ${m.H}″ high)`],
      m.seats ? ['Seating', `Up to ${m.seats}${m.lounger ? ` · ${m.lounger} lounger${m.lounger > 1 ? 's' : ''}` : ''}`] : null,
      ['Hydrotherapy', this.seriesCapEl.textContent + ` (${SERIES[this.state.series].label})`],
      ['Water capacity', m.waterCap],
      ['Dry weight', m.dryWeight],
      ['Shell', SHELLS[this.state.shell].label],
      ['Cabinet', CABINETS[this.state.cabinet].label],
    ].filter(Boolean);
    this.specsEl.innerHTML = rows.map(([k, v]) => `<div class="sb3-spec"><span>${k}</span><strong>${v}</strong></div>`).join('');

    this.badgesEl.innerHTML = FAMILIES[m.family].badges.map((t) => `<span class="sb3-badge">${t}</span>`).join('');
  }

  /** Mirror the build into the page's contact/pricing form (hidden fields). */
  syncForm() {
    const form = document.querySelector('form[action*="/contact"]');
    if (!form) return;
    const m = this.modelObj;
    const ser = m.series[this.state.series];
    const fields = {
      '3D Build — Model': `${m.name} (${FAMILIES[m.family].label})`,
      '3D Build — Series': SERIES[this.state.series].label,
      '3D Build — Jets': ser.jetsLabel || `${ser.jets} jets / ${ser.pumps} pumps`,
      '3D Build — Shell Colour': SHELLS[this.state.shell].label,
      '3D Build — Cabinet Colour': CABINETS[this.state.cabinet].label,
    };
    Object.keys(fields).forEach((key) => {
      const name = `contact[${key}]`;
      let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = fields[key];
    });
  }

  _readURL() {
    try {
      const p = new URLSearchParams(location.search);
      return {
        model: p.get('model'), series: p.get('series'),
        shell: p.get('shell'), cabinet: p.get('cabinet'),
      };
    } catch (e) { return {}; }
  }

  _writeURL() {
    try {
      const p = new URLSearchParams(location.search);
      p.set('model', this.state.model);
      p.set('series', this.state.series);
      p.set('shell', this.state.shell);
      p.set('cabinet', this.state.cabinet);
      history.replaceState(null, '', `${location.pathname}?${p}${location.hash}`);
    } catch (e) { /* sandboxed iframes etc. */ }
  }
}

function swatchButton(label, background) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'sb3-swatch';
  b.innerHTML = `<span class="sb3-swatch-dot" style="background:${background}"></span><span class="sb3-swatch-label">${label}</span>`;
  return b;
}

/** Place pillows along the bowl walls (skipping lounger sides). */
function pillowPositions(model, bowlL, bowlW, count, loungers) {
  const out = [];
  const inset = 3.2;
  if (model.type === 'swimspa') {
    for (let i = 0; i < count; i++) {
      out.push({ x: -bowlL / 2 + inset, z: (i - (count - 1) / 2) * 20, yaw: Math.PI / 2 });
    }
    return out;
  }
  /* hot tub: candidate wall midpoints + corners, skip lounger walls (±Z) */
  const spots = [
    { x: 0, z: -bowlW / 2 + inset, yaw: 0, wall: 'north' },
    { x: -bowlL / 2 + inset, z: 0, yaw: Math.PI / 2, wall: 'west' },
    { x: bowlL / 2 - inset, z: 0, yaw: -Math.PI / 2, wall: 'east' },
    { x: 0, z: bowlW / 2 - inset, yaw: Math.PI, wall: 'south' },
    { x: -bowlL / 4, z: -bowlW / 2 + inset, yaw: 0, wall: 'north' },
    { x: bowlL / 4, z: -bowlW / 2 + inset, yaw: 0, wall: 'north' },
    { x: -bowlL / 4, z: bowlW / 2 - inset, yaw: Math.PI, wall: 'south' },
    { x: bowlL / 4, z: bowlW / 2 - inset, yaw: Math.PI, wall: 'south' },
  ];
  const blocked = [];
  if (loungers >= 1) blocked.push('south');
  if (loungers >= 2) blocked.push('north');
  const ok = spots.filter((s) => !blocked.includes(s.wall));
  for (let i = 0; i < Math.min(count, ok.length); i++) out.push(ok[i]);
  /* loungers get their own head pillow at the top of the slope */
  if (loungers >= 1) out.push({ x: -bowlL * 0.34, z: bowlW / 2 - inset, yaw: Math.PI });
  if (loungers >= 2) out.push({ x: bowlL * 0.34, z: -bowlW / 2 + inset, yaw: 0 });
  return out;
}

/* ================================================================== *
 * BOOT
 * ================================================================== */

let threePromise = null;
function loadThree() {
  if (!threePromise) threePromise = import(/* @vite-ignore */ THREE_URL);
  return threePromise;
}

async function boot(root, isPrimary) {
  const loading = root.querySelector('[data-sb3-loading]');
  const fallback = root.querySelector('[data-sb3-fallback]');
  const canvas = root.querySelector('canvas.sb3-canvas');
  try {
    const THREE = await loadThree();
    RoundedRectCurve = RoundedRectCurve || makeRoundedRectCurveClass(THREE);

    const engine = new SpaScene(THREE, canvas);
    const stage = canvas.parentElement;
    const doResize = () => engine.resize(stage.clientWidth, stage.clientHeight);
    new ResizeObserver(doResize).observe(stage);
    doResize();

    /* pause rendering while off-screen */
    new IntersectionObserver((entries) => {
      entries.forEach((en) => { engine.active = en.isIntersecting; });
    }, { rootMargin: '100px' }).observe(stage);

    const ui = new BuilderUI(root, engine, isPrimary);
    root.__sb3 = { engine, ui }; /* debug/verification handle */

    /* CTA smooth-scroll to the on-page pricing form */
    const cta = root.querySelector('[data-sb3-cta]');
    if (cta) {
      cta.addEventListener('click', (ev) => {
        const href = cta.getAttribute('href') || '';
        if (href.startsWith('#')) {
          const target = document.querySelector(href) || document.querySelector('form[action*="/contact"]');
          if (target) {
            ev.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    }

    if (loading) loading.classList.add('sb3-hidden');
  } catch (err) {
    /* WebGL unavailable or CDN unreachable */
    if (loading) loading.classList.add('sb3-hidden');
    if (fallback) fallback.hidden = false;
    if (canvas) canvas.style.display = 'none';
    console.error('[spa-builder-3d]', err);
  }
}

function setup() {
  let primary = true;
  document.querySelectorAll('[data-sb3-root]').forEach((root) => {
    if (root.dataset.sb3Init === '1') return;
    root.dataset.sb3Init = '1';
    const isPrimary = primary;
    primary = false;
    /* Boot now if the section is already near the viewport (also covers
       environments where IO callbacks are throttled); otherwise defer via IO. */
    const r = root.getBoundingClientRect();
    const near = r.top < window.innerHeight + 600 && r.bottom > -600;
    if (near || !('IntersectionObserver' in window)) {
      boot(root, isPrimary);
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            io.disconnect();
            boot(root, isPrimary);
          }
        });
      }, { rootMargin: '600px 0px' });
      io.observe(root);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setup);
} else {
  setup();
}
